/**
 * Адреса редактора новой темы без входа: правка блюда (?edit), раздела
 * (/pg/…), групп модификаторов, экран разделов (?viewMode=adminProductGroups).
 * Без админ-сессии такая страница была пустой или показывала форму, которую
 * нельзя сохранить. Теперь — экран входа, как в панели («Мы вас не узнаем»,
 * те же ключи adminLogin), и переход в меню заведения. С ?a=ключ сессия
 * появляется, и открывается редактор.
 */
import React, { PropsWithChildren, useEffect, useState } from 'react'
import Link from 'next/link'
import { Trans } from 'react-i18next'
import { useAppContext } from '@/pages/_app'
import { useVenueContext } from '@/services/venue'
import { makeFreshchat } from '@/services/freshchat'
import { makeJivosite } from '@/services/jivosite'
import { uiEvents } from '@/utils/event'

/** адрес открыт как адрес редактора — проверка по адресу при открытии страницы */
export function useEditorAddress(isEditorUrl: (url: URL) => boolean) {
  const [isEditor, setEditor] = useState(false)
  useEffect(() => {
    setEditor(isEditorUrl(new URL(location.href)))
  }, [])
  return isEditor
}

/** правка блюда: /p/…?edit */
export function isProductEditUrl(url: URL) {
  return url.searchParams.has('edit')
}

/** экран разделов и прочие виды редактора меню: ?viewMode=admin… */
export function isMenuEditorUrl(url: URL) {
  return (url.searchParams.get('viewMode') ?? '').startsWith('admin')
}

/** страница целиком редакторская — без входа экран входа */
export function withAdminGate<P extends object>(Component: React.ComponentType<P>) {
  return function AdminGated(props: P) {
    return (
      <ModernAdminGate>
        <Component {...props} />
      </ModernAdminGate>
    )
  }
}

/** страница общая с гостем — экран входа, только если открыт адрес редактора */
export function withAdminGateWhen<P extends object>(
  isEditorUrl: (url: URL) => boolean,
  Component: React.ComponentType<P>,
) {
  return function AdminGatedWhen(props: P) {
    const isEditor = useEditorAddress(isEditorUrl)
    return (
      <ModernAdminGate when={isEditor}>
        <Component {...props} />
      </ModernAdminGate>
    )
  }
}

export function ModernAdminGate(props: PropsWithChildren<{ when?: boolean }>) {
  const { admin } = useVenueContext()!
  if (props.when === false) {
    return <>{props.children}</>
  }
  const state = admin.authorizationStatus.state.type
  // пока сессия проверяется, не мелькаем ни формой, ни экраном входа
  if (state === 'loading') {
    return null
  }
  if (state !== 'ok') {
    return <ModernAdminLogin />
  }
  return <>{props.children}</>
}

function ModernAdminLogin() {
  const { t } = useAppContext()
  const { venue, admin, menuRoute } = useVenueContext()!
  const { authorizationStatus } = admin

  // виджет поддержки — как на экране входа в панель
  useEffect(() => {
    uiEvents.next({ type: 'AppLoaded' })
    if (venue.features.freshChatToken) {
      makeFreshchat({
        token: venue.features.freshChatToken,
        authorizationStatus,
        venue,
      }).initScript()
    }
    if (venue.features.jivositeToken) {
      makeJivosite({ token: venue.features.jivositeToken, authorizationStatus, venue }).initScript()
    }
  }, [])

  return (
    <main className="admin-login">
      <div className="admin-login__card">
        <h1 className="admin-login__title">{t('adminLogin:adminArea')}</h1>
        <div className="admin-login__text">
          <Trans
            t={t}
            i18nKey="adminLogin:manualForEmail"
            components={{
              p: <p />,
              aHowToPay: (
                <a
                  href="https://foodeon.com/ru/blog/tpost/zss8olhnd1-kak-oplatit-servis-foodeon"
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        </div>
        <Link className="vas-submit admin-login__menu" href={menuRoute.path}>
          {t('venueName:seeMenu')}
        </Link>
        <button
          type="button"
          className="admin-login__help"
          onClick={() => {
            uiEvents.next({ type: 'RequestSupportWidget', from: 'loginPage' })
          }}
        >
          {t('common:needHelp')}
        </button>
      </div>
    </main>
  )
}
